import { useEffect } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type Theme = "dark" | "light" | "system";

const STORAGE_KEY = "vite-ui-theme";

type ThemeStore = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const getSystemTheme = (): "dark" | "light" => {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const applyThemeToDocument = (theme: Theme) => {
  const root = document.documentElement;
  root.classList.remove("light", "dark");

  const effectiveTheme = theme === "system" ? getSystemTheme() : theme;
  root.classList.add(effectiveTheme);
};

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      theme: "system",
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const useTheme = () => {
  const { theme, setTheme } = useThemeStore();

  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  return { theme, setTheme } as const;
};
